import axios from "axios";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";
import { Credentials, User } from "typing";
import { TRACKING_API_BASE_URL } from "../services/trackingApi";
import { loginSuccess, logoutUser } from "../actions/authentication";
import { Store } from "../store";
import { appLocalStorage } from "../utils/AppLocalStorage";
import { ADMIN, COMPANY, getEffectiveUserRole } from "../utils/roles";

const TOKEN_EXPIRATION_TIME = 2; //in hours
const ACCESS_TOKEN = process.env.REACT_APP_SUCCOTH_API_ACCESS_TOKEN;
const API_BASE_URL = process.env.REACT_APP_SUCCOTH_API_BASE_URL;

export function useAuthentication() {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const {
    state: { user },
    dispatch,
  } = useContext(Store);
  const { mutate } = useSWRConfig();

  const createAccessHeader = useCallback(
    async (apiKey?: string) => {
      try {
        const token = user ? user?.auth.access_token : ACCESS_TOKEN;
        return {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };
      } catch (error) {
        navigate("/");
        console.log(error);
      }
    },
    [navigate, user]
  );

  type sendRequestProps = {
    method: string;
    url: string;
    data?: any;
  };

  const sendRequest = useCallback(
    async ({ method, url, data }: sendRequestProps) => {
      return axios({
        url: url,
        method: method,
        headers: await createAccessHeader(),
        data: data,
      })
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            console.log("Request timed out");
          } else {
            console.log(error.message);
            return error.response?.data?.message || error.message;
          }
        });
    },
    [createAccessHeader]
  );

  async function getCurrentAuthenticatedUser() {
    const response = appLocalStorage.getItem("userProfile");
    return response;
  }

  /**
   * SignIn
   */

  const saveUserInSession = useCallback(
    async (user: User) => {
      const tokenExpirationDate = new Date();
      tokenExpirationDate.setHours(
        tokenExpirationDate.getHours() + TOKEN_EXPIRATION_TIME
      );
      if (user) {
        user.auth.tokenExpirationDate = tokenExpirationDate;
        const tokenData = user;

        dispatch(loginSuccess(tokenData));
      }
    },
    [dispatch]
  );

  const signIn = useCallback(
    async ({ username, password }: Credentials) => {
      try {
        const url = `${TRACKING_API_BASE_URL}/api/token/`;
        const res = await axios.post(
          url,
          {
            // L'endpoint Tracking accepte username ou email
            username,
            email: username,
            password,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        let response = res.data;

        if (!response?.error_code && response?.message !== "confirm_email") {
          // La réponse Tracking est déjà au format attendu: { user, auth, must_change_password, ... }
          let userData = response as User;

          // Enrichir le profil avec les informations métier Admin (company_id, institution_id, etc.)
          // en appelant l'API Admin avec le même token, si disponible.
          try {
            const accessToken = userData?.auth?.access_token;
            const userId = userData?.user?.id;

            if (API_BASE_URL && accessToken && userId) {
              const adminRes = await axios.get(`${API_BASE_URL}/users/profile/${userId}`, {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
              });

              const adminProfile = adminRes.data || {};

              // Fusion : on garde en priorité les rôles/app_permissions non vides.
              // Si Tracking renvoie roles: [] ou app_permissions vides, on utilise ceux d'Admin
              // (évite le 403 pour superadmin quand Tracking n'a pas de rôles spécifiques)
              const trackingRoles = userData.user?.roles;
              const trackingPerms = userData.user?.app_permissions;
              const hasTrackingRoles = Array.isArray(trackingRoles) && trackingRoles.length > 0;
              const hasTrackingPerms = Array.isArray(trackingPerms) && trackingPerms.length > 0;

              userData = {
                ...userData,
                user: {
                  ...adminProfile,
                  roles: hasTrackingRoles ? trackingRoles : (adminProfile.roles ?? trackingRoles),
                  app_permissions: hasTrackingPerms ? trackingPerms : (adminProfile.app_permissions ?? trackingPerms),
                },
              } as User;
            }
          } catch (err) {
            // En cas d'erreur sur le profil Admin, on ne bloque pas le login Tracking.
            console.warn("Erreur lors de l'enrichissement du profil Admin:", err);
          }

          saveUserInSession(userData);
        }
        setLoading(false);
        return response;
      } catch (err: any) {
        setLoading(false);
        console.log("ERRORRRRR", err);
        return {
          message: err?.response?.data?.message || err?.message || "Erreur de connexion",
          error_code: err?.response?.data?.error_code ?? "login_error",
        };
      }
    },
    [saveUserInSession]
  );
  const signUp = useCallback(
    async (data: any) => {
      try {
        const result = await sendRequest({
          url: `${API_BASE_URL}/users/register/`,
          data,
          method: "post",
        });
        const response = await result;
        setLoading(false);
        return response;
      } catch (err) {
        setLoading(false);
        console.log("ERRORRRRR", err);
      }
    },
    [sendRequest]
  );
  const emailVerification = useCallback(
    async ({ token, otp }: { token: string; otp: string }) => {
      try {
        const result = sendRequest({
          url: `${API_BASE_URL}/users/verify-token/`,
          data: { token, otp },
          method: "post",
        });
        const response = await result;
        setLoading(false);
        return response;
      } catch (err) {
        setLoading(false);
        console.log("ERRORRRRR", err);
      }
    },
    [sendRequest]
  );
  const forgotPassword = useCallback(
    async ({ username }: { username: string }) => {
      try {
        const result = await sendRequest({
          url: `${API_BASE_URL}/users/password/request/`,
          data: { email: username },
          method: "post",
        });
        const response = await result;
        setLoading(false);
        return response;
      } catch (err) {
        setLoading(false);
        console.log("ERRORRRRR", err);
      }
    },
    [sendRequest]
  );

  const setNewPassword = useCallback(
    async ({ password, token }: { password: string; token: string }) => {
      try {
        const result = await sendRequest({
          url: `${API_BASE_URL}/users/password/set/`,
          data: { token, password },
          method: "post",
        });
        const response = await result;
        setLoading(false);
        return response;
      } catch (err) {
        setLoading(false);
        console.log("ERRORRRRR", err);
      }
    },
    [sendRequest]
  );

  /**
   * SignOut
   */

  const clearCacheData = () => {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  };

  const deleteLoggedInSession = useCallback(async () => {
    // appLocalStorage.clearStorage();
    clearCacheData();
  }, []);

  const signOut = useCallback(async () => {
    try {
      const userRole = getEffectiveUserRole(user);
      if (userRole === ADMIN || userRole === COMPANY) {
        navigate("/");
      } else {
        navigate("/");
      }
      

      mutate(
        (key) => true, // which cache keys are updated
        undefined, // update cache data to `undefined`
        { revalidate: false } // do not revalidate
      );
      dispatch(logoutUser());

      await deleteLoggedInSession();
      setLoading(false);
    } catch (ex) {
      setLoading(false);
      console.log(ex);
    }
  }, [dispatch, navigate, deleteLoggedInSession, user, mutate]);

  const hasRole = useCallback(
    (role: string) => {
      setLoading(false);
      const effectiveRole = getEffectiveUserRole(user);
      const normalizedRole = String(role || "").toLowerCase().replace(/\s/g, "");
      return !!effectiveRole && effectiveRole === normalizedRole;
    },
    [user]
  );

  const hasPermission = useCallback(
    (model: string, method: string) => {
      let result = false;
      const permissions = user?.app_permissions ?? user?.user?.app_permissions;
      permissions?.forEach((element: any) => {
        if (element.model === model && element.permission === method) {
          result = true;
        }
      });
      return result;
    },
    [user]
  );

  useEffect(() => {
    const tokenExipryDate = new Date(user?.auth?.tokenExpirationDate);
    const currentDate = new Date();
    if (tokenExipryDate.getTime() < currentDate.getTime()) {
      signOut();
    }
  }, [user, signOut]);

  return {
    loading,
    user: user as User,
    hasRole,
    hasPermission,
    authProvider: {
      signUp,
      signIn,
      signOut,
      getCurrentAuthenticatedUser,
      forgotPassword,
      setNewPassword,
      emailVerification,
    },
  };
}
